/**
 * API client.
 *
 * Structure follows plugins/augurly/svelte/src/lib/api.js (build-time base-URL
 * resolution, one request helper, one auth-request helper), with three fixes:
 *
 *  1. No hardcoded localhost fallback that overrides the configured origin —
 *     the STRAO api is on :8100, not :8000, and a silent fallback to the wrong
 *     port is a confusing failure. Config comes from .env.* only.
 *  2. The 401 handler exists once, not copy-pasted per helper.
 *  3. Proactive token refresh before expiry (see authUtils.shouldRefresh).
 */

import {
  clearAuth,
  getAuthHeaders,
  getToken,
  isTokenValid,
  setToken,
  shouldRefresh,
} from './authUtils.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/strao';
const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_BASE_URL;

if (!API_BASE_URL || !AUTH_API_BASE) {
  console.error(
    'STRAO Portal: VITE_API_BASE_URL / VITE_AUTH_API_BASE_URL are unset. ' +
    'The bundle was built without an .env file for this mode.'
  );
}

const API_BASE = `${API_BASE_URL}${API_PREFIX}`;

export { API_BASE_URL, API_PREFIX };

/** Where to send an unauthenticated user. Relative to the Flask mount. */
const LOGIN_PATH = '/strao/login';

function redirectToLogin() {
  clearAuth();
  if (!window.location.pathname.endsWith('/login')) {
    window.location.href = LOGIN_PATH;
  }
}

/** FastAPI returns `detail` as a string, an object, or a list of field errors. */
function normalizeDetail(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d.loc) ? d.loc.join('.') : d.loc;
        return loc ? `${loc}: ${d.msg}` : d.msg;
      })
      .join('; ');
  }
  return detail.message || detail.error || JSON.stringify(detail);
}

let refreshInFlight = null;

/** Renew the JWT via chassis core, coalescing concurrent callers. */
async function refreshTokenIfNeeded() {
  const token = getToken();
  if (!token || !isTokenValid(token) || !shouldRefresh(token)) return;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${AUTH_API_BASE}/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.access_token) setToken(data.access_token);
        }
      } catch {
        // Refresh is best-effort: if it fails the existing token is still valid
        // for now, and the next 401 will handle it.
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

async function request(url, options = {}, { withAuth = true, onUnauthorized = redirectToLogin } = {}) {
  if (withAuth) await refreshTokenIfNeeded();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(withAuth ? getAuthHeaders() : {}),
      ...(options.headers || {}),
    },
  });

  if (response.status === 401 && withAuth) {
    onUnauthorized();
    throw new Error('Unauthorized — please sign in again');
  }

  if (response.status === 204) return null;

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const error = new Error(
      normalizeDetail(payload?.detail) || `Request failed (${response.status})`
    );
    error.status = response.status;
    error.detail = payload?.detail;
    throw error;
  }

  return payload;
}

function qs(params = {}) {
  const clean = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  if (!clean.length) return '';
  return `?${new URLSearchParams(clean).toString()}`;
}

const portal = (path, params) => `${API_BASE}${path}${qs(params)}`;
// Auth endpoints are chassis core's, not the plugin's, and must never carry the
// 401-redirect behaviour — that would loop on the login page itself.
const authRequest = (path, options) =>
  request(`${AUTH_API_BASE}${path}`, options, { withAuth: false });

export const api = {
  // ---- auth (chassis core /auth/*) ----
  login: (email, password) =>
    authRequest('/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  requestMagicLink: (email) =>
    authRequest('/magic-link-request', { method: 'POST', body: JSON.stringify({ email }) }),

  verifyMagicLink: (token) =>
    authRequest('/magic-link-verify', { method: 'POST', body: JSON.stringify({ token }) }),

  register: (data) =>
    authRequest('/register', { method: 'POST', body: JSON.stringify(data) }),

  verifyEmailAndLogin: (token) =>
    authRequest('/verify-email-and-login', { method: 'POST', body: JSON.stringify({ token }) }),

  forgotPassword: (email) =>
    authRequest('/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),

  resetPassword: (token, newPassword) =>
    authRequest('/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    }),

  changePassword: (currentPassword, newPassword) =>
    request(`${AUTH_API_BASE}/change-password`, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  getProfile: () => request(`${AUTH_API_BASE}/me`),

  updateProfile: (data) =>
    request(`${AUTH_API_BASE}/me`, { method: 'PUT', body: JSON.stringify(data) }),

  logout: () => request(`${AUTH_API_BASE}/logout`, { method: 'POST' }),

  // ---- portal context ----
  getContext: (tenantId) => request(portal('/me/context', { tenant_id: tenantId })),

  // ---- properties ----
  listProperties: (params) => request(portal('/properties', params)),
  getProperty: (id, params) => request(portal(`/properties/${id}`, params)),
  getPropertyKnowledge: (id, params) => request(portal(`/properties/${id}/knowledge`, params)),

  // ---- conversations ----
  listConversations: (params) => request(portal('/conversations', params)),
  getConversation: (id, params) => request(portal(`/conversations/${id}`, params)),

  // ---- reservations ----
  listReservations: (params) => request(portal('/reservations', params)),

  // ---- review queue (staff only) ----
  //
  // approve/reject/override all delegate server-side to str_autoresponder's
  // real send path. Do NOT repoint these at /str-autoresponder/approvals/*:
  // that endpoint marks the row reviewed and never sends, so the guest would
  // receive nothing while the UI reported success.
  listReview: (params) => request(portal('/review', params)),
  getReviewItem: (id, params) => request(portal(`/review/${id}`, params)),
  approveReviewItem: (id, notes, params) =>
    request(portal(`/review/${id}/approve`, params), {
      method: 'POST', body: JSON.stringify({ notes: notes ?? null }),
    }),
  rejectReviewItem: (id, notes, params) =>
    request(portal(`/review/${id}/reject`, params), {
      method: 'POST', body: JSON.stringify({ notes: notes ?? null }),
    }),
  overrideReviewItem: (id, overrideText, notes, params) =>
    request(portal(`/review/${id}/override`, params), {
      method: 'POST',
      body: JSON.stringify({ override_text: overrideText, notes: notes ?? null }),
    }),

  // ---- unlinked records (staff only) ----
  getUnlinkedSummary: (params) => request(portal('/unlinked', params)),
  listUnmatchedTally: (params) => request(portal('/unlinked/tally', params)),
  resolveTallySubmission: (id, propertyId, params) =>
    request(portal(`/unlinked/tally/${id}/resolve`, params), {
      method: 'POST', body: JSON.stringify({ property_id: propertyId }),
    }),
  listUnlinkedConversations: (params) => request(portal('/unlinked/conversations', params)),
  resolveConversationProperty: (id, propertyId, params) =>
    request(portal(`/unlinked/conversations/${id}/resolve`, params), {
      method: 'POST', body: JSON.stringify({ property_id: propertyId }),
    }),

  // ---- tenant settings ----
  getSettings: (params) => request(portal('/settings', params)),
  updateSettings: (data, params) =>
    request(portal('/settings', params), { method: 'PATCH', body: JSON.stringify(data) }),
  // Super-user only. Flips only the DB half of the send gate; the env half is
  // a deployment concern and is reported read-only in the response.
  setRealSends: (allow, reason, params) =>
    request(portal('/settings/real-sends', params), {
      method: 'POST',
      body: JSON.stringify({ allow_real_sends: allow, reason: reason ?? null }),
    }),

  // ---- saved replies / canned situations (staff only) ----
  //
  // `variants` is sent whole on update, and each entry keeps its `id`. Do not
  // strip the ids to "simplify" the payload: the server matches by id, and an
  // id-less variant is inserted as a new row — which nulls canned_variant_id on
  // every past response that used it and resets rotation history.
  listSituations: (params) => request(portal('/situations', params)),
  createSituation: (data, params) =>
    request(portal('/situations', params), {
      method: 'POST', body: JSON.stringify(data),
    }),
  updateSituation: (id, data, params) =>
    request(portal(`/situations/${id}`, params), {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  deleteSituation: (id, params) =>
    request(portal(`/situations/${id}`, params), { method: 'DELETE' }),
  // Read-only: runs the real classifier against the saved replies, changes
  // nothing, writes no audit row.
  testSituationMatch: (message, params) =>
    request(portal('/situations/test', params), {
      method: 'POST', body: JSON.stringify({ message }),
    }),

  // ---- rules for all properties (staff only; plans/STRAO_DRAFT_QUALITY_PLAN.md, T1) ----
  listRules: (params) => request(portal('/rules', params)),
  createRule: (data, params) =>
    request(portal('/rules', params), { method: 'POST', body: JSON.stringify(data) }),
  updateRule: (id, data, params) =>
    request(portal(`/rules/${id}`, params), { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRule: (id, params) =>
    request(portal(`/rules/${id}`, params), { method: 'DELETE' }),

  // ---- test chat (super-user only; simulates, never sends) ----
  testChatTurn: (body, params) =>
    request(portal('/test-chat/turn', params), {
      method: 'POST', body: JSON.stringify(body),
    }),

  // ---- pipeline observability (staff only, read-only) ----
  getPipelineHealth: (params) => request(portal('/observability', params)),
  listProcessingLog: (params) => request(portal('/observability/processing', params)),
  listSyncJobs: (params) => request(portal('/observability/sync-jobs', params)),

  // ---- metrics ----
  getPerformance: (params) => request(portal('/metrics/performance', params)),

  // ---- super-user administration ----
  listPortalUsers: (params) => request(portal('/admin/users', params)),
  setPropertyGrants: (userId, propertyIds, params) =>
    request(portal(`/admin/users/${userId}/properties`, params), {
      method: 'POST',
      body: JSON.stringify({ property_ids: propertyIds }),
    }),
  deactivateUser: (userId, params) =>
    request(portal(`/admin/users/${userId}/deactivate`, params), { method: 'POST' }),
  listInvitations: (params) => request(portal('/admin/invitations', params)),
  createInvitation: (data, params) =>
    request(portal('/admin/invitations', params), {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  revokeInvitation: (id, params) =>
    request(portal(`/admin/invitations/${id}/revoke`, params), { method: 'POST' }),
  listAudit: (params) => request(portal('/admin/audit', params)),
  resendInvitation: (id, params) =>
    request(portal(`/admin/invitations/${id}/resend`, params), { method: 'POST' }),

  // ---- super-user tenant administration ----
  listTenants: (params) => request(portal('/admin/tenants', params)),
  createTenant: (data) =>
    request(portal('/admin/tenants'), { method: 'POST', body: JSON.stringify(data) }),
  getTenant: (id) => request(portal(`/admin/tenants/${id}`)),
  updateTenant: (id, data) =>
    request(portal(`/admin/tenants/${id}`), { method: 'PATCH', body: JSON.stringify(data) }),
  deactivateTenant: (id, reason) =>
    request(portal(`/admin/tenants/${id}/deactivate`), {
      method: 'POST', body: JSON.stringify({ reason }),
    }),
  reactivateTenant: (id, reason) =>
    request(portal(`/admin/tenants/${id}/reactivate`), {
      method: 'POST', body: JSON.stringify({ reason }),
    }),
  listTenantMembers: (id) => request(portal(`/admin/tenants/${id}/members`)),
  changeMemberRole: (tenantId, userId, role) =>
    request(portal(`/admin/tenants/${tenantId}/members/${userId}`), {
      method: 'PATCH', body: JSON.stringify({ role }),
    }),
  deactivateMember: (tenantId, userId) =>
    request(portal(`/admin/tenants/${tenantId}/members/${userId}/deactivate`), { method: 'POST' }),
  reactivateMember: (tenantId, userId) =>
    request(portal(`/admin/tenants/${tenantId}/members/${userId}/reactivate`), { method: 'POST' }),
  // Integrations. Secrets are write-only: sent here, never returned.
  getTenantHostaway: (id) => request(portal(`/admin/tenants/${id}/hostaway`)),
  setTenantHostaway: (id, data) =>
    request(portal(`/admin/tenants/${id}/hostaway`), { method: 'PUT', body: JSON.stringify(data) }),
  testTenantHostaway: (id) =>
    request(portal(`/admin/tenants/${id}/hostaway/test`), { method: 'POST' }),
  getTenantWebhook: (id) => request(portal(`/admin/tenants/${id}/hostaway/webhook`)),
  registerTenantWebhook: (id) =>
    request(portal(`/admin/tenants/${id}/hostaway/webhook`), { method: 'POST' }),
  getTenantTally: (id) => request(portal(`/admin/tenants/${id}/tally`)),
  setTenantTallyKey: (id, apiKey) =>
    request(portal(`/admin/tenants/${id}/tally/api-key`), {
      method: 'PUT', body: JSON.stringify({ api_key: apiKey }),
    }),
  addTenantTallyForm: (id, data) =>
    request(portal(`/admin/tenants/${id}/tally/forms`), { method: 'POST', body: JSON.stringify(data) }),
  updateTenantTallyForm: (id, rowId, data) =>
    request(portal(`/admin/tenants/${id}/tally/forms/${rowId}`), {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  testTenantTally: (id, rowId) =>
    request(portal(`/admin/tenants/${id}/tally/test`), {
      method: 'POST', body: JSON.stringify({ row_id: rowId ?? null }),
    }),
  getTenantNotifications: (id) => request(portal(`/admin/tenants/${id}/notifications`)),
  setTenantNotifications: (id, data) =>
    request(portal(`/admin/tenants/${id}/notifications`), { method: 'PUT', body: JSON.stringify(data) }),
  testTenantNotification: (id, channel) =>
    request(portal(`/admin/tenants/${id}/notifications/test`), {
      method: 'POST', body: JSON.stringify({ channel }),
    }),

  // Operations
  getTenantReadiness: (id, live = false) =>
    request(portal(`/admin/tenants/${id}/readiness`, { live: live || undefined })),
  createTenantAutoresponderConfig: (id) =>
    request(portal(`/admin/tenants/${id}/autoresponder-config`), { method: 'POST' }),
  setTenantLifecycle: (id, status, reason) =>
    request(portal(`/admin/tenants/${id}/lifecycle`), {
      method: 'POST', body: JSON.stringify({ status, reason }),
    }),
  listTenantJobs: (id) => request(portal(`/admin/tenants/${id}/jobs`)),
  runTenantJob: (id, jobId) =>
    request(portal(`/admin/tenants/${id}/jobs/${jobId}/run`), { method: 'POST' }),
  getTenantActivity: (id, days = 7) => request(portal(`/admin/tenants/${id}/activity`, { days })),
  setMemberProperties: (tenantId, userId, propertyIds) =>
    request(portal(`/admin/tenants/${tenantId}/members/${userId}/properties`), {
      method: 'POST', body: JSON.stringify({ property_ids: propertyIds }),
    }),
};

export default api;
